import { useEffect, useState } from 'react'
import { BookOpen, CalendarDays, Camera, ChevronRight, ExternalLink, Heart, Sparkles, User, Users } from 'lucide-react'
import type { CastPortrait } from './castPortraits'
import { buildCatalogProfilePresentation } from './catalogProfilePresentation'
import { timelineForPerson } from './castProfileTimelines'
import type { CastProfile } from './castProfiles'
import OfficialSocialEmbed from './OfficialSocialEmbed'
import type { PersonProfilePayload, ProfileEpisode } from './profileTypes'
import { publishedOriginStatusSource } from './profileRelationshipStatus'

type CastProfileContentProps = {
  profile?: CastProfile
  portrait?: CastPortrait
  displayName: string
  record: PersonProfilePayload | null
  loading: boolean
  error: string
  onOpenPerson: (personId: string, name: string) => void
  onOpenRelationship: (relationshipId: string) => void
  onOpenEpisode: (episode: ProfileEpisode) => void
}

function episodeLabel(episode: ProfileEpisode) {
  return episode.episode === null ? 'Special' : `Episode ${episode.episode}`
}

function airDateLabel(value: string | null) {
  if (!value) return 'Air date unavailable'
  const date = new Date(`${value}T12:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date)
}

export default function CastProfileContent({
  profile,
  portrait,
  displayName,
  record,
  loading,
  error,
  onOpenPerson,
  onOpenRelationship,
  onOpenEpisode,
}: CastProfileContentProps) {
  const [activeSeasonKey, setActiveSeasonKey] = useState('')
  const generated = buildCatalogProfilePresentation(record, displayName)
  const editorialTimeline = profile ? timelineForPerson(profile.personId) : undefined
  const timeline = editorialTimeline ?? {
    connections: generated.connections,
    seasons: generated.seasons,
  }
  const shortName = portrait?.shortName ?? generated.shortName
  const headline = profile?.headline ?? generated.headline
  const bio = profile?.bio ?? generated.bio
  const originStory = profile?.originStory ?? generated.originStory
  const isStoryIdentity = profile?.entryKind === 'story_identity'
  const statusSource = publishedOriginStatusSource(record, editorialTimeline)

  useEffect(() => {
    setActiveSeasonKey(record?.seasons[0]?.key ?? '')
  }, [record?.person.id, record?.seasons])

  const activeSeason = record?.seasons.find((season) => season.key === activeSeasonKey) ?? record?.seasons[0]

  // Added spinoff chapters sit in the chronology of the original story rather
  // than after its most recent chapter. Source dates come from the live guide.
  const chapters = [...timeline.seasons].sort((left, right) => {
    const premiere = (show: string, season: number) => record?.seasons
      .find((candidate) => candidate.show === show && candidate.season === season)
      ?.episodes.map((episode) => episode.air_date).filter((date): date is string => Boolean(date)).sort()[0]
    const leftDate = premiere(left.show, left.season)
    const rightDate = premiere(right.show, right.season)
    return leftDate && rightDate ? leftDate.localeCompare(rightDate) : 0
  })

  function matchingSeason(show: string, season: number) {
    return record?.seasons.find((candidate) => candidate.show.toLocaleLowerCase() === show.toLocaleLowerCase() && candidate.season === season)
  }

  function browseSeason(show: string, season: number) {
    const match = matchingSeason(show, season)
    if (!match) return
    setActiveSeasonKey(match.key)
    window.requestAnimationFrame(() => document.getElementById('cast-profile-episodes-heading')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
  }

  return (
    <div className="cast-profile">
      <section className="cast-profile__intro" aria-labelledby="cast-profile-bio-heading">
        {isStoryIdentity && <span className="cast-profile__section-kicker"><Sparkles size={14} /> THE STORY CONTEXT</span>}
        <h3 id="cast-profile-bio-heading">{isStoryIdentity ? `About the ${shortName} storyline` : `Meet ${shortName}`}</h3>
        <p className="cast-profile__headline">{headline}</p>
        <div className="cast-profile__bio">
          {bio.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
        </div>
      </section>

      <section className="cast-profile__origin" aria-labelledby="cast-profile-origin-heading">
        <span className="cast-profile__origin-icon"><Heart size={18} /></span>
        <div>
          <h3 id="cast-profile-origin-heading">{isStoryIdentity ? 'How the story began' : 'How they met'}</h3>
          <p>{originStory}</p>
        </div>
      </section>

      {!portrait && (
        <div className="portrait-coming">
          <Camera size={18} />
          <div>
            <strong>{isStoryIdentity ? 'No verified identity portrait' : 'Portrait curation in progress'}</strong>
            <span>{isStoryIdentity ? 'Pictures used by an online persona are not proof of who sent the messages. This entry does not assign those pictures to a verified partner.' : 'The full catalog profile is live now; a verified, creator-controlled social portrait will be added separately.'}</span>
          </div>
        </div>
      )}

      {timeline.connections.length > 0 && (
          <section className="cast-profile__section" aria-labelledby="cast-profile-connections-heading">
            <header className="cast-profile__section-heading">
              <div>
                <span className="cast-profile__section-kicker"><Users size={14} /> CONNECTED CAST</span>
                <h3 id="cast-profile-connections-heading">People in {shortName}&apos;s story</h3>
              </div>
              <small>{timeline.connections.length} {timeline.connections.length === 1 ? 'person' : 'people'}</small>
            </header>
            <div className="cast-profile__connections">
              {timeline.connections.map((connection) => (
                <article key={`${connection.relationshipId ?? 'pending'}-${connection.personId ?? connection.name}`} className="cast-profile__connection-card">
                  {connection.personId ? (
                    <button type="button" className="cast-profile__person-link" onClick={() => onOpenPerson(connection.personId!, connection.name)}>
                      <span className="cast-profile__person-avatar">{connection.name.split(/\s+/).map((part) => part[0]).slice(0, 2).join('')}</span>
                      <span>
                        <small>CAST PROFILE</small>
                        <strong>{connection.name}</strong>
                        <em>View {connection.name.split(/\s+/)[0]}&apos;s page</em>
                      </span>
                      <ChevronRight size={19} />
                    </button>
                  ) : (
                    <div className="cast-profile__person-link cast-profile__person-link--pending">
                      <span className="cast-profile__person-avatar">{connection.name.split(/\s+/).map((part) => part[0]).slice(0, 2).join('')}</span>
                      <span><small>CONNECTED CAST</small><strong>{connection.name}</strong><em>Profile verification in progress</em></span>
                    </div>
                  )}
                  <p>{connection.context}</p>
                  {connection.relationshipId && (
                    <button type="button" className="cast-profile__couple-link" onClick={() => onOpenRelationship(connection.relationshipId!)}>
                      <Heart size={13} /> Open their couple journey <ChevronRight size={13} />
                    </button>
                  )}
                </article>
              ))}
            </div>
          </section>
      )}

      {timeline.seasons.length > 0 && (
          <section className="cast-profile__section" aria-labelledby="cast-profile-journey-heading">
            <header className="cast-profile__section-heading">
              <div>
                <span className="cast-profile__section-kicker"><CalendarDays size={14} /> SEASON TIMELINE</span>
                <h3 id="cast-profile-journey-heading">The story, season by season</h3>
              </div>
              <small>{timeline.seasons.length} {timeline.seasons.length === 1 ? 'season' : 'seasons'}</small>
            </header>
            <div className="cast-profile__season-timeline">
              {chapters.map((chapter, index) => {
                const seasonRecord = matchingSeason(chapter.show, chapter.season)
                const connectedPeople = timeline.connections.filter((connection) => chapter.partnerPersonIds?.includes(connection.personId ?? ''))
                return (
                  <article className="cast-profile__season-card" key={`${chapter.show}-${chapter.season}`}>
                    <span className="cast-profile__timeline-index">{index + 1}</span>
                    <div className="cast-profile__season-card-copy">
                      <div className="cast-profile__season-meta">
                        <span>{chapter.show}</span>
                        <strong>Season {chapter.season}</strong>
                        {chapter.status === 'announced' && <em>ANNOUNCED</em>}
                      </div>
                      <h4>{chapter.title}</h4>
                      <p>{chapter.recap}</p>
                      <footer>
                        <div className="cast-profile__season-people">
                          {connectedPeople.map((connection) => (
                            <button type="button" key={connection.name} onClick={() => onOpenPerson(connection.personId!, connection.name)}>
                              <User size={12} /> {connection.name} <ChevronRight size={11} />
                            </button>
                          ))}
                        </div>
                        {seasonRecord && (
                          <button type="button" className="cast-profile__browse-season" onClick={() => browseSeason(chapter.show, chapter.season)}>
                            <BookOpen size={13} /> Browse {seasonRecord.episodes.length} {seasonRecord.episodes.length === 1 ? 'episode' : 'episodes'}
                          </button>
                        )}
                      </footer>
                    </div>
                  </article>
                )
              })}
            </div>
          </section>
      )}

      <section className="cast-profile__section" aria-labelledby="cast-profile-episodes-heading">
        <header className="cast-profile__section-heading">
          <div>
            <span className="cast-profile__section-kicker"><BookOpen size={14} /> EPISODE GUIDE</span>
            <h3 id="cast-profile-episodes-heading">Episodes in credited seasons</h3>
          </div>
          {record && <small>{record.episode_count} season {record.episode_count === 1 ? 'ep' : 'eps'}</small>}
        </header>
        <div className="cast-profile__accuracy-note">
          <CalendarDays size={16} />
          <p><strong>Credited-season view</strong> {isStoryIdentity ? 'These are episodes from seasons associated with this catalog storyline, not proof that an identified person appeared in each episode.' : <>These are episodes from {shortName}&apos;s credited seasons. We&apos;re verifying individual on-screen appearances before marking them as exact credits.</>}</p>
        </div>

        {loading && <div className="cast-profile__loading" aria-label="Loading episode guide"><i /><i /><i /><span>Connecting the season history…</span></div>}
        {!loading && error && <p className="cast-profile__episode-empty">{error}</p>}
        {!loading && !error && record && record.seasons.length === 0 && <p className="cast-profile__episode-empty">Season credits are being organized for this profile.</p>}
        {!loading && !error && record && record.seasons.length > 0 && (
          <>
            <div className="cast-profile__season-tabs" role="tablist" aria-label={`${shortName}'s credited seasons`}>
              {record.seasons.map((season) => (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeSeason?.key === season.key}
                  className={activeSeason?.key === season.key ? 'is-active' : ''}
                  key={season.key}
                  onClick={() => setActiveSeasonKey(season.key)}
                >
                  <span>{season.show}</span>
                  <strong>Season {season.season}</strong>
                </button>
              ))}
            </div>
            {activeSeason && (
              <div className="cast-profile__episodes" role="tabpanel">
                <div className="cast-profile__episodes-title">
                  <span><strong>{activeSeason.show}</strong> · Season {activeSeason.season}</span>
                  <small>{activeSeason.episodes.length} {activeSeason.episodes.length === 1 ? 'episode' : 'episodes'}</small>
                </div>
                {activeSeason.episodes.length > 0 ? activeSeason.episodes.map((episode) => (
                  <button type="button" key={episode.id} onClick={() => onOpenEpisode(episode)}>
                    <span className="cast-profile__episode-number">{episode.episode ?? 'SP'}</span>
                    <span>
                      <small>{episodeLabel(episode)}{episode.is_tell_all ? ' · Tell All' : ''}</small>
                      <strong>{episode.title}</strong>
                      <em>{airDateLabel(episode.air_date)}</em>
                    </span>
                    <ChevronRight size={17} />
                  </button>
                )) : <p className="cast-profile__episode-empty">The episode list for this season is still being indexed.</p>}
              </div>
            )}
          </>
        )}
      </section>

      {portrait && (
        <section className="cast-profile__section cast-profile__portrait" aria-labelledby="cast-profile-portrait-heading">
          <header className="cast-profile__section-heading">
            <div>
              <span className="cast-profile__section-kicker">OFFICIAL SOCIAL</span>
              <h3 id="cast-profile-portrait-heading">From {portrait.shortName}&apos;s public account</h3>
            </div>
          </header>
          <OfficialSocialEmbed portrait={portrait} />
        </section>
      )}

      <footer className="cast-profile__sources">
        <span>{profile ? 'PROFILE SOURCES' : 'CATALOG PROFILE · SEASONS AND CONNECTIONS INDEXED'}</span>
        <div>
          {statusSource && (
            <a href={statusSource.url} target="_blank" rel="noreferrer">Relationship status source{statusSource.asOf ? ` · ${statusSource.asOf}` : ''} <ExternalLink size={12} /></a>
          )}
          {profile?.sources.map((source) => (
            <a key={source.url} href={source.url} target="_blank" rel="noreferrer">{source.label} <ExternalLink size={12} /></a>
          ))}
          {!profile && record?.person.source_url && (
            <a href={record.person.source_url} target="_blank" rel="noreferrer">Catalog source <ExternalLink size={12} /></a>
          )}
        </div>
        <p>{profile
          ? 'Bio copy is original Journey90 editorial writing based on the linked cast introductions, interviews and reporting.'
          : 'This friendly profile summary is generated from Journey90’s indexed cast, relationship and season records; current relationship status is never inferred from social media.'}</p>
      </footer>
    </div>
  )
}
